/**
 * AreasPanel — GEN-EKB-017: «Mi empresa · Conocimiento · Ámbitos y áreas» (pantalla provisional;
 * la definitiva llega en GEN-EKB-018/019). Árbol de áreas con icono y nombre y los KBs de cada una.
 * Solo el administrador ve las acciones (+ Área, renombrar/icono, mover, retirar, mover KB):
 * llegan si `puede_gestionar`, pero la puerta real es la revalidación del kernel (R-PT-04).
 * Retirar no borra; si el área tiene KBs o subáreas se pregunta el área destino.
 */

import { useCallback, useEffect, useState } from 'react'
import { crearArea, editarArea, getAreas, moverArea, moverKbDeArea, retirarArea } from '../api/client.js'

const MENSAJES = {
  nombre_invalido: 'El nombre es obligatorio.',
  icono_invalido: 'El icono es un emoji.',
  descripcion_invalida: 'La descripción es de una línea (máx. 140 caracteres).',
  ciclo_areas: 'Un área no puede colgar de sí misma ni de una de sus subáreas.',
  destino_requerido: 'El área tiene KBs o subáreas: elige el área destino.',
  destino_invalido: 'Ese destino no vale.',
  area_sistema: '«Sin clasificar» es un área de sistema: no se renombra, mueve ni retira.',
  area_solo_kbs: '«Sin clasificar» solo admite KBs: no puede tener subáreas.',
  area_retirada: 'El área está retirada.',
  area_no_encontrada: 'El área ya no existe: recarga la pantalla.',
  ekb_no_disponible: 'La base de conocimiento aún no está disponible en esta instalación.',
  forbidden: 'Solo el administrador gestiona las áreas.',
}
const mensaje = err => MENSAJES[err?.code] || err?.message || 'No se ha podido completar la operación.'

// Áreas en orden de árbol, con su profundidad.
function aplanar(areas) {
  const hijos = new Map()
  for (const a of areas) hijos.set(a.padre_id ?? null, [...(hijos.get(a.padre_id ?? null) ?? []), a])
  const ids = new Set(areas.map(a => a.id))
  const salida = []
  const bajar = (padre, nivel) => (hijos.get(padre) ?? []).forEach(a => { salida.push({ ...a, nivel }); bajar(a.id, nivel + 1) })
  bajar(null, 0)
  // Un área cuyo padre no está en la lista (no debería pasar) se pinta en la raíz, no se pierde.
  areas.filter(a => a.padre_id && !ids.has(a.padre_id)).forEach(a => { salida.push({ ...a, nivel: 0 }); bajar(a.id, 1) })
  return salida
}

function descendientes(areas, id) {
  const fuera = new Set([id])
  let crecio = true
  while (crecio) {
    crecio = false
    for (const a of areas) if (a.padre_id && fuera.has(a.padre_id) && !fuera.has(a.id)) { fuera.add(a.id); crecio = true }
  }
  return fuera
}

// GEN-PX-020d: qué cuelga de un área (KBs activos y subáreas directas), de los datos de getAreas.
function contenidoDe(areas, id) {
  const real = areas.find(a => a.id === id)
  return { real, kbs: real?.kbs ?? [], subareas: areas.filter(a => a.padre_id === id) }
}

const cuenta = (n, uno, varios, items) => {
  const nombres = items.slice(0, 3).map(i => i.nombre).join(', ') + (items.length > 3 ? '…' : '')
  return `${n} ${n === 1 ? uno : varios}${n ? ` (${nombres})` : ''}`
}

// GEN-PX-020: la lógica de las acciones sobre áreas (formulario + llamadas) se comparte con Saber.
export function useGestionAreas({ areas, onHecho }) {
  const [accion, setAccion] = useState(null) // { tipo: 'nueva'|'editar'|'mover'|'retirar'|'kb', area?, kb? }
  const [error, setError] = useState(null)
  const [campos, setCampos] = useState({})

  // Destino preseleccionado al retirar: la subárea vuelve a su padre; la raíz con solo KBs, a «Sin clasificar»;
  // la raíz con subáreas no se adivina (el usuario elige).
  function destinoInicial(area) {
    const { kbs, subareas } = contenidoDe(areas, area.id)
    if (!kbs.length && !subareas.length) return ''
    if (area.padre_id) return area.padre_id
    return subareas.length ? '' : (areas.find(a => a.sistema)?.id ?? '')
  }

  function abrir(tipo, extra = {}) {
    setError(null)
    // GEN-PX-020c: el área puede venir del `mapa` de Saber (sin descripción ni padre): los datos reales son los de `areas`
    // (getAreas), también al guardar, para no pisar lo que el usuario no ha tocado.
    const real = extra.area ? areas.find(a => a.id === extra.area.id) : null
    const area = real ? { ...extra.area, ...real } : extra.area
    setAccion({ tipo, ...extra, area })
    setCampos({
      nombre: area?.nombre ?? '', icono: area?.icono ?? '', descripcion: area?.descripcion ?? '',
      padre: tipo === 'mover' ? (area.padre_id ?? '') : (extra.padre ?? ''),
      destino: tipo === 'retirar' ? destinoInicial(area) : '', area: '',
    })
  }

  async function enviar(e) {
    e.preventDefault()
    setError(null)
    const { tipo, area, kb } = accion
    try {
      if (tipo === 'nueva') await crearArea({ nombre: campos.nombre.trim(), descripcion: campos.descripcion.trim(), icono: campos.icono.trim(), padre_id: campos.padre })
      if (tipo === 'editar') {
        // Solo viaja lo que el usuario ha cambiado (además del nombre): si la descripción real no se pudo cargar, no se borra.
        const cambios = { nombre: campos.nombre.trim() }
        if (campos.icono.trim() !== (area.icono ?? '').trim()) cambios.icono = campos.icono.trim()
        if (campos.descripcion.trim() !== (area.descripcion ?? '').trim()) cambios.descripcion = campos.descripcion.trim()
        await editarArea(area.id, cambios)
      }
      if (tipo === 'mover') await moverArea(area.id, campos.padre)
      if (tipo === 'retirar') {
        // Sin contenido no hay destino: no viaja `destino_id`.
        const { kbs, subareas } = contenidoDe(areas, area.id)
        if (kbs.length || subareas.length) await retirarArea(area.id, campos.destino)
        else await retirarArea(area.id)
      }
      if (tipo === 'kb') await moverKbDeArea(kb.id, campos.area)
      setAccion(null)
      await onHecho?.()
    } catch (err) {
      setError(mensaje(err))
    }
  }

  const cerrar = () => setAccion(null)
  const poner = campo => e => setCampos(c => ({ ...c, [campo]: e.target.value }))
  const opciones = (excluir, { sistema = true } = {}) =>
    aplanar(areas).filter(a => !excluir.has(a.id) && (sistema || !a.sistema))
      .map(a => <option key={a.id} value={a.id}>{`${'— '.repeat(a.nivel)}${a.icono} ${a.nombre}`}</option>)

  // GEN-PX-020b: el mismo formulario se pinta en línea (Ámbitos, con su título) o dentro de un Modal (Saber, que pone el título).
  function titulo() {
    if (!accion) return null
    const { tipo, area, kb } = accion
    if (tipo === 'nueva') return 'Nueva área'
    if (tipo === 'editar') return `Renombrar o cambiar icono de «${area.nombre}»`
    if (tipo === 'mover') return `Mover «${area.nombre}» de padre`
    if (tipo === 'retirar') return `Retirar «${area.nombre}»`
    return `Mover el KB «${kb.nombre}» a otra área`
  }

  function formulario(enModal) {
    if (!accion) return null
    const { tipo, area, kb } = accion
    const { kbs: kbsArea, subareas: subsArea } = area ? contenidoDe(areas, area.id) : { kbs: [], subareas: [] }
    const tieneContenido = kbsArea.length > 0 || subsArea.length > 0
    return (
      <form className={enModal ? 'dk-modal__form' : 'equipo-nuevo'} onSubmit={enviar} aria-label="Acción sobre áreas">
        {!enModal && <h3>{titulo()}</h3>}
        {(tipo === 'nueva' || tipo === 'editar') && (
          <>
            <label>Nombre <input required maxLength={120} autoComplete="off" value={campos.nombre} onChange={poner('nombre')} disabled={area?.sistema} /></label>
            <label>Icono (emoji) <input maxLength={16} autoComplete="off" value={campos.icono} onChange={poner('icono')} placeholder="📁" /></label>
            <label>Descripción (una línea) <input maxLength={140} autoComplete="off" value={campos.descripcion} onChange={poner('descripcion')} /></label>
          </>
        )}
        {(tipo === 'nueva' || tipo === 'mover') && (
          <label>
            Área padre
            <select value={campos.padre} onChange={poner('padre')}>
              <option value="">Ninguna (primer nivel)</option>
              {opciones(tipo === 'mover' ? descendientes(areas, area.id) : new Set(), { sistema: false })}
            </select>
          </label>
        )}
        {tipo === 'retirar' && (
          tieneContenido ? (
            <>
              <p>
                «{area.nombre}» tiene {cuenta(kbsArea.length, 'KB', 'KBs', kbsArea)} y {cuenta(subsArea.length, 'subárea', 'subáreas', subsArea)}.
                {' '}{subsArea.length > 0 && !area.padre_id ? 'Al retirarla, elige a qué área pasan:' : 'Al retirarla, pasan a:'}
              </p>
              <select required aria-label="Área destino de sus KBs y subáreas" value={campos.destino} onChange={poner('destino')}>
                <option value="">Elige el destino</option>
                {opciones(descendientes(areas, area.id), { sistema: subsArea.length === 0 })}
              </select>
            </>
          ) : <p>¿Retirar el área «{area.nombre}»? No tiene KBs ni subáreas.</p>
        )}
        {tipo === 'kb' && (
          <label>
            Área
            <select required value={campos.area} onChange={poner('area')}>
              <option value="">Elige el área</option>
              {opciones(new Set([kb.areaActual]))}
            </select>
          </label>
        )}
        {error && <p className={enModal ? 'dk-modal__error' : 'equipo-estado'} role="alert">{error}</p>}
        <div className={enModal ? 'dk-modal__acciones' : 'equipo-nuevo__acciones'}>
          {enModal ? (
            <>
              <button type="button" className="dk-btn" onClick={cerrar}>Cancelar</button>
              <button type="submit" className="dk-btn dk-btn--primario">{tipo === 'retirar' ? 'Retirar' : 'Guardar'}</button>
            </>
          ) : (
            <>
              <button type="submit" className="btn btn--primary">{tipo === 'retirar' ? 'Retirar' : 'Guardar'}</button>
              <button type="button" className="btn btn--ghost" onClick={cerrar}>Cancelar</button>
            </>
          )}
        </div>
      </form>
    )
  }

  return {
    abrir, cerrar, accion, error,
    formulario: formulario(false),
    // Para envolver en <Modal titulo rotulo>: sin título propio y con los estilos del modal.
    formularioModal: formulario(true),
    titulo: titulo(),
    rotulo: accion?.tipo === 'kb' ? 'KB · Bloque de conocimiento' : 'Área',
  }
}

export default function AreasPanel() {
  const [datos, setDatos] = useState(null)

  const cargar = useCallback(async () => {
    try {
      setDatos(await getAreas())
    } catch (err) {
      setDatos({ error: mensaje(err) })
    }
  }, [])
  useEffect(() => { cargar() }, [cargar])

  const areas = datos?.areas ?? []
  const gestiona = Boolean(datos?.puede_gestionar)
  const { abrir, formulario, accion, error } = useGestionAreas({ areas, onHecho: cargar })

  return (
    <section className="mec-visor">
      <h2>Ámbitos y áreas</h2>
      <p className="equipo-estado">Los temas estables de tu empresa. Cada KB vive en un área; los que aún no tienen sitio están en «Sin clasificar».</p>
      {!datos && <p className="equipo-estado">Cargando…</p>}
      {datos?.error && <p className="equipo-estado" role="alert">{datos.error}</p>}
      {!accion && error && <p className="equipo-estado" role="alert">{error}</p>}
      {gestiona && !accion && <button type="button" className="btn btn--primary" onClick={() => abrir('nueva')}>+ Área</button>}
      {formulario}

      <ul className="areas-arbol">
        {aplanar(areas).map(a => (
          <li key={a.id} style={{ marginLeft: `${a.nivel * 1.5}rem` }}>
            <strong>{a.icono} {a.nombre}</strong>
            {a.descripcion && <span className="equipo-estado"> — {a.descripcion}</span>}
            {gestiona && (
              <span>
                {' '}
                {!a.sistema && <button type="button" className="btn btn--ghost" onClick={() => abrir('nueva', { padre: a.id })}>+ Subárea</button>}
                {!a.sistema && <button type="button" className="btn btn--ghost" onClick={() => abrir('editar', { area: a })}>Renombrar / icono</button>}
                {!a.sistema && <button type="button" className="btn btn--ghost" onClick={() => abrir('mover', { area: a })}>Mover de padre</button>}
                {!a.sistema && <button type="button" className="btn btn--ghost" onClick={() => abrir('retirar', { area: a })}>Retirar</button>}
              </span>
            )}
            <ul>
              {a.kbs.length === 0 && <li className="equipo-estado">Sin KBs</li>}
              {a.kbs.map(k => (
                <li key={k.id}>
                  {k.nombre}
                  {gestiona && (
                    <>
                      {' '}
                      <button type="button" className="btn btn--ghost" aria-label={`Mover ${k.nombre} a otra área`}
                        onClick={() => abrir('kb', { kb: { ...k, areaActual: a.id } })}>Mover a otra área</button>
                    </>
                  )}
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
      {datos && !datos.error && areas.length === 0 && <p className="equipo-estado">Todavía no hay áreas.</p>}
    </section>
  )
}
